// ============================================================
// THE ATTACK'S SPECIALS ON THE PLATE (plan body-map H3): the weapon's
// special rules merged into one moment over the burst (engine/plate/
// specials) - each special's look (./registry) at its place, in its turn.
// Drawn once when the burst arrives (CSS delays), inside the figure so it
// moves with it.
// ============================================================

import type { CSSProperties } from 'react';
import { figureMove } from '../../../../engine/plate/specials';
import { middleOf, type PlateFrame, type Point } from '../../../../engine/plate/frame';
import type { Guard } from '../../../../engine/plate/moments';
import sx from '../../../../styles/PlateSpecials.module.css';
import { impetusSide } from './blades';
import { seeded } from '../../../../engine/plate/hash';
import type { HitPlay } from '../../usePlateHits';
import { SpecialLook } from './registry';
import { swingAcross, type LookCtx } from './kit';
import { timed } from '../timing';

interface Props {
  u: string;
  play: HitPlay;
  frame: PlateFrame;
  /** Daemonic protection (Exorcist runs over all of it) and the body's size (Titanbane shakes the ground by it). */
  guard: Guard;
}

/** Every special of the attack, at its place, in its turn. */
export default function SpecialMoments({ u, play, frame, guard }: Props) {
  const { moments } = play;
  if (!moments.length) return null;
  const middle = middleOf(frame);
  return (
    <g key={play.id} data-testid="plate-specials">
      <filter id={`${u}-sglow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation={10 * frame.k} result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      {/* A power field's disruption: the part's lines thrown out of shape. */}
      <filter id={`${u}-disrupt`} x="-30%" y="-30%" width="160%" height="160%">
        <feTurbulence type="turbulence" baseFrequency={0.02 / frame.k} numOctaves={2} seed={3} result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale={160 * frame.k} xChannelSelector="R" yChannelSelector="G" />
      </filter>
      {/* Numbing's blur over the head. */}
      <filter id={`${u}-numb`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation={26 * frame.k} />
      </filter>
      {/* An ethereal blade's mist. */}
      <filter id={`${u}-mist`} x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency={0.015 / frame.k} numOctaves={2} seed={6} result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale={60 * frame.k} xChannelSelector="R" yChannelSelector="G" />
      </filter>
      {moments.map((m, i) => {
        const id = `${u}-sp${i}`;
        const ctx: LookCtx = {
          u,
          id,
          at: frame.anchors[m.location] ?? middle,
          part: m.location,
          partBox: frame.partBox(m.location),
          figureBox: frame.box,
          start: m.at,
          k: frame.k,
          type: m.type,
          lines: frame.lines,
          daemonic: guard.daemonic > 0,
          anchors: frame.anchors,
          sizeSteps: guard.sizeSteps,
          r: seeded(`${play.id}:${i}`),
          glow: `url(#${u}-sglow)`,
        };
        return <SpecialLook key={i} name={m.key} ctx={ctx} />;
      })}
    </g>
  );
}

/** Each move's class: the bench's figMove sequences as one-shot keyframes. */
const MOVE_CLASS: Record<string, string> = {
  shock: sx.moveShock,
  impetus: sx.moveImpetus,
  graviton: sx.moveGraviton,
  concussive: sx.moveConcussive,
  powerfield: sx.movePf,
};

/** How far the Power Field shoves the figure along the swing, Explosive farthest (on Energy it never moves). */
const PF_PUSH = { X: 300, I: 170, R: 120, E: 0 };

/**
 * The one move the attack makes of the whole figure (engine/plate/specials `figureMove`), as the class and style for
 * the group that wraps the figure - keyed by the burst, so it plays once - or nothing. The Power Field shoves along
 * the swing its look draws (the same seed, the same part), Impetus to the side its trail shows.
 */
export function moveOf(
  play: HitPlay,
  frame: PlateFrame,
  feet: Point,
): { className: string; style: CSSProperties } | null {
  const move = figureMove(play.moments);
  if (!move) return null;
  const r = seeded(`${play.id}:${move.index}`);
  const swing = swingAcross(frame.partBox(move.location), r).ang;
  const push = PF_PUSH[move.type];
  return {
    className: `${sx.move} ${MOVE_CLASS[move.key]}`,
    style: {
      ...timed(move.at),
      transformOrigin: `${feet[0]}px ${feet[1]}px`,
      '--side': impetusSide(r),
      '--mx': Math.round(Math.cos(swing) * push),
      '--my': Math.round(Math.sin(swing) * push - (move.type === 'X' ? 60 : 0)),
    } as CSSProperties,
  };
}
